import {
  InfiniteData,
  QueryKey,
  UseBaseQueryOptions,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { produce } from "immer";
import { capitalize, chunk } from "lodash-es";
import { v4 } from "uuid";

import { useUploadFile } from "src/api/filesApi/queries";
import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import { CaseActionFormValues } from "src/cases/caseView/schema";
import {
  CaseDeserialized,
  CasesDeserialized,
  TaskIdExtractor,
} from "src/cases/queries/casesTypes";
import { useCasesClient } from "src/cases/queries/client";
import { caseQueuesKeys } from "src/cases/queries/queues";
import { useCasesQueueParams } from "src/cases/queues/hooks/useCasesQueueParams";
import { CaseQueueSummary } from "src/cases/queues/types";
import { sortingToApiSortParam } from "src/cases/queues/utils";
import {
  AttributesValue,
  CaseActionFormPatchResponse,
  CaseBulkAtomicOperationsRequestAtomicOperationsInner,
  CaseBulkRefTypeEnum,
  CaseCreateAcceptedResponse,
  CaseCreateRequest,
  CasePatchRequest,
  CasePatchResponse,
  CaseResponse,
  CaseRunActionAtomicOperationOpEnum,
  CasesResponse,
  CaseTaskAttributes,
  CaseTaskCompletionPatchResponse,
  CaseTaskFormPatchResponse,
  FileCategoriesAtomicOperation,
  FileCategoriesAtomicOperationOpEnum,
  FileCategoriesAtomicOperationsRequest,
  FileCategoriesAtomicResourceObjectTypeEnum,
  FileCategoriesAtomicResultsResponse,
  GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum,
  JsonApiAtomicResult,
  ModelsCaseDataApiModelsRelatedEntitiesBlockConfigPatchDataTypeEnum,
  RelatedEntitiesBlockConfigPatch,
  RelatedEntitiesBlockConfigPatchResponse,
  RelatedEntitiesCaseBlockConfigPatchAttributes,
  RelatedEventsBlockConfigPatch,
  RelatedEventsBlockConfigPatchResponse,
  RelatedEventsCaseBlockConfigPatchAttributes,
  RunningActionsResponse,
} from "src/clients/case-management";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import { toastActions } from "src/design-system/Toast/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { DashboardPageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { FileSource } from "src/utils/files";
import { assertUnreachable } from "src/utils/typeUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

// Constants
const STALE_TIME = 1000 * 60 * 5; // 5 minutes

// HTTP status codes that should not trigger a retry
const NO_RETRY_STATUS_CODES = [401, 403, 404, 409, 410] as const;

const BATCH_SIZE = 100;

// Helper for executing bulk operations in batches
const executeBulkOperationsInBatches = async (
  apiClient: ReturnType<typeof useCasesClient>,
  queueId: string,
  caseIds: string[],
  atomicOperations: CaseBulkAtomicOperationsRequestAtomicOperationsInner[],
) => {
  if (!apiClient) {
    throw new Error("API client not available");
  }

  const operationBatches = chunk(atomicOperations, BATCH_SIZE);
  const batchCaseIds = chunk(caseIds, BATCH_SIZE);

  const results = await Promise.allSettled(
    operationBatches.map(async (batch) => {
      const response =
        await apiClient.applyBulkAtomicOperationsApiV1CasesOperationsQueueQueueIdPost(
          {
            queueId,
            caseBulkAtomicOperationsRequest: {
              "atomic:operations": batch,
            },
          },
        );
      return response.data;
    }),
  );

  return { results, batchCaseIds };
};

// Query key factory for cache management
export const casesKeys = {
  all: (workspaceId: string) => ["cases", workspaceId] as const,
  get: (workspaceId: string, caseId: string) =>
    [...casesKeys.all(workspaceId), "case", caseId] as const,
  list: (
    workspaceId: string,
    environment: string,
    filters?: Record<string, string>,
  ) => [...casesKeys.all(workspaceId), "list", environment, filters] as const,
  // Root for every "cases related to an entity" list (the Entity view Cases
  // tab). Use it as an invalidation/update prefix to target them all at once.
  relatedAll: (workspaceId: string) =>
    [...casesKeys.all(workspaceId), "related"] as const,
  // Prefix for a single entity's related-cases lists, across all sort orders.
  // Invalidate this to refresh every sort variant (e.g. the Cases table and
  // the count pill, which may request different sorts) in one call.
  relatedEntity: (
    workspaceId: string,
    environment: string,
    entityType: string,
    entityId: string,
  ) =>
    [
      ...casesKeys.relatedAll(workspaceId),
      environment,
      entityType,
      entityId,
    ] as const,
  related: (
    workspaceId: string,
    environment: string,
    entityType: string,
    entityId: string,
    sort?: string,
  ) =>
    [
      ...casesKeys.relatedEntity(
        workspaceId,
        environment,
        entityType,
        entityId,
      ),
      sort,
    ] as const,
};

// Type definitions
type CaseQueryOptions<TData = CaseResponse> = UseBaseQueryOptions<
  CaseResponse,
  AxiosError,
  TData
>;

const useCasesWorkspaceParams = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCasesClient();

  return { wsId, apiClient };
};

const getSerializedData = (data: CaseResponse) => {
  return deserializeJsonApi<CaseDeserialized>(data);
};

/**
 * Increments etag value handling both string and number types from API
 * Remove this helper when backend consistently sends etags as single type
 */
export const incrementEtag = (currentEtag: string | number): string => {
  const etagString = String(currentEtag);
  return (parseInt(etagString, 10) + 1).toString();
};

const FAKE_ETAG = "cache_bust_etag";

// Main hooks
export const useCaseRaw = <TData = CaseResponse>(
  caseId?: string | null,
  options?: Omit<CaseQueryOptions<TData>, "queryKey" | "queryFn">,
) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryKey = casesKeys.get(wsId, caseId || "");

  return useQuery<CaseResponse, AxiosError, TData>({
    queryKey,
    queryFn: async (context) => {
      if (!apiClient || !caseId) {
        throw new Error("Missing required parameters");
      }

      const cachedCase = context.client.getQueryData<CaseResponse>(queryKey);

      try {
        const response = await apiClient.getCaseApiV1CasesIdGet({
          id: caseId,
          // Override browser's automatic If-None-Match on first fetch to bypass Cloudflare cache
          ...(cachedCase == null && { ifNoneMatch: FAKE_ETAG }),
        });

        return response.data;
      } catch (error) {
        // Axios treats 304 as an error
        if (
          isAxiosError(error) &&
          error.response?.status === 304 &&
          cachedCase
        ) {
          return cachedCase;
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Don't retry on certain HTTP status codes (auth errors, not found, outdated, etc.)
      if (isAxiosError(error) && error.response?.status) {
        const status = error.response.status;
        if (
          NO_RETRY_STATUS_CODES.includes(
            status as (typeof NO_RETRY_STATUS_CODES)[number],
          )
        ) {
          return false;
        }
      }
      // Default retry logic: retry up to 3 times for other errors
      return failureCount < 3;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled: !!apiClient && !!caseId && options?.enabled !== false,
  });
};

type UseQueryOptions = UseBaseQueryOptions<
  CaseResponse,
  AxiosError,
  ReturnType<typeof getSerializedData>
>;

export const useCase = (
  caseId: string | undefined | null,
  options?: Omit<UseQueryOptions, "queryKey" | "queryFn">,
) => {
  return useCaseRaw(caseId, {
    select: getSerializedData,
    ...options,
  });
};

export const CASES_FOR_ENTITY_DEFAULT_SORT = "-created_at";

type UseCasesForEntityParams = {
  entityId: string;
  entityType: string;
  environment: GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum;
  sort?: string;
  enabled?: boolean;
};

const CASES_FOR_ENTITY_PAGE_SIZE = 100;

// Flatten all fetched pages and deserialize into a single list. `deserialize
// JsonApi` already hoists and returns the array for list responses.
const selectCasesForEntity = (data: InfiniteData<CasesResponse>) =>
  deserializeJsonApi<CasesDeserialized["data"]>({
    data: data.pages.flatMap((page) => page.data),
    included: data.pages.flatMap((page) => page.included ?? []),
  });

export const useCasesForEntity = ({
  entityId,
  entityType,
  environment,
  sort = CASES_FOR_ENTITY_DEFAULT_SORT,
  enabled = true,
}: UseCasesForEntityParams) => {
  const { apiClient } = useCasesWorkspaceParams();
  const { workspace } = useWorkspaceContext();

  return useInfiniteQuery<CasesResponse, AxiosError, CasesDeserialized["data"]>(
    {
      queryKey: casesKeys.related(
        workspace.id,
        environment,
        entityType,
        entityId,
        sort,
      ),
      queryFn: async ({ pageParam, signal }) => {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const response = await apiClient.getCasesByEntityApiV1CasesRelatedGet(
          {
            filterEnv: environment,
            filterEntityType: entityType,
            filterEntityId: entityId,
            sort,
            pageSize: CASES_FOR_ENTITY_PAGE_SIZE,
            pageCursor: pageParam as string,
          },
          { signal },
        );

        return response.data;
      },
      select: selectCasesForEntity,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (lastPage.links.next) {
          const url = new URL(lastPage.links.next);
          return new URLSearchParams(url.search).get("page[cursor]");
        }
      },
      staleTime: STALE_TIME,
      enabled: !!apiClient && enabled,
    },
  );
};

type UpdateCaseParams = {
  id: string;
  assigneeId?: string | null;
  queueId?: string | null;
  ifMatch?: string | null;
};

type UpdateCaseContext = {
  previousQueueCases?: InfiniteData<CasesResponse>;
  previousCase?: CaseResponse;
  previousCaseQueueSummary?: CaseQueueSummary;
  previousEntityCases?: [QueryKey, InfiniteData<CasesResponse> | undefined][];
};

export const useUpdateCase = (currentQueueId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();
  const [environment] = useEnvironment();
  const { filterParams: filters, sorting } =
    useCasesQueueParams(currentQueueId);
  const sort = sortingToApiSortParam(sorting);
  const userId = useCurrentUserId();

  return useMutation<
    CasePatchResponse,
    AxiosError,
    UpdateCaseParams,
    UpdateCaseContext
  >({
    mutationFn: async (params) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const casePatchRequest: CasePatchRequest = {
        data: {
          type: "cases" as const,
          relationships: {
            ...(params.assigneeId !== undefined && {
              assignee: {
                data: params.assigneeId
                  ? { type: "assignee", id: params.assigneeId }
                  : null,
              },
            }),
            ...(params.queueId !== undefined && {
              queue: {
                data: params.queueId
                  ? { type: "queue", id: params.queueId }
                  : null,
              },
            }),
          },
        },
      };

      const response = await apiClient.patchCaseApiV1CasesIdPatch({
        id: params.id,
        casePatchRequest,
        ifMatch: params.ifMatch,
      });
      return response.data;
    },
    onMutate: (variables) => {
      const previousCaseQueueSummary =
        queryClient.getQueryData<CaseQueueSummary>(
          caseQueuesKeys.summary(wsId, currentQueueId, environment),
        );

      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, variables.id),
      );

      const previousQueueCases = queryClient.getQueryData<
        InfiniteData<CasesResponse>
      >(caseQueuesKeys.cases(wsId, currentQueueId, environment, filters, sort));

      if (previousCaseQueueSummary) {
        queryClient.setQueryData<CaseQueueSummary>(
          caseQueuesKeys.summary(wsId, currentQueueId, environment),
          (oldSummary) => {
            return produce(oldSummary, (draft) => {
              if (!draft) return;

              const isAssignment = !!variables.assigneeId;
              const isUnassignment = variables.assigneeId === null;
              const previousAssignee =
                previousCase?.data.relationships?.assignee.data ??
                previousQueueCases?.pages
                  .flatMap((page) => page.data)
                  .find((c) => c.id === variables.id)?.relationships?.assignee
                  .data;

              if (isAssignment) {
                if (!previousAssignee) {
                  draft.attributes.unassigned -= 1;
                }

                if (
                  variables.assigneeId === userId &&
                  previousAssignee?.id !== userId
                ) {
                  draft.attributes.assigned_to_user += 1;
                } else if (
                  variables.assigneeId !== userId &&
                  previousAssignee?.id === userId
                ) {
                  draft.attributes.assigned_to_user -= 1;
                }
              } else if (isUnassignment) {
                draft.attributes.unassigned += 1;

                if (previousAssignee?.id === userId) {
                  draft.attributes.assigned_to_user -= 1;
                }
              }
            });
          },
        );
      }

      // Optimistically write the new assignee + a bumped etag onto the mutated
      // case wherever it appears in a paginated cases list.
      const applyAssigneeToPage = (page: CasesResponse) => {
        const caseIndex = page.data.findIndex(
          (caseData) => caseData.id === variables.id,
        );
        if (caseIndex === -1) return;

        const data = page.data[caseIndex];
        if (data.relationships) {
          data.relationships.assignee = updateAssignee(variables.assigneeId);
        }

        // Increment etag - API sends strings but type says number
        if (data.attributes?.etag !== undefined) {
          (data.attributes as { etag: string | number }).etag = incrementEtag(
            data.attributes.etag,
          );
        }
      };

      if (previousQueueCases && variables.assigneeId !== undefined) {
        queryClient.setQueryData<InfiniteData<CasesResponse>>(
          caseQueuesKeys.cases(
            wsId,
            currentQueueId,
            environment,
            filters,
            sort,
          ),
          (oldCases) => {
            return produce(oldCases, (draft) => {
              draft?.pages.forEach(applyAssigneeToPage);
            });
          },
        );
      }

      // The Entity Cases tab holds a separate, never-refetched cases list
      // (eventual consistency), so patch it optimistically too. The prefix
      // covers every entity's list.
      const relatedCasesFilter = { queryKey: casesKeys.relatedAll(wsId) };
      const previousEntityCases =
        queryClient.getQueriesData<InfiniteData<CasesResponse>>(
          relatedCasesFilter,
        );
      if (variables.assigneeId !== undefined) {
        queryClient.setQueriesData<InfiniteData<CasesResponse>>(
          relatedCasesFilter,
          (oldCases) => {
            return produce(oldCases, (draft) => {
              draft?.pages.forEach(applyAssigneeToPage);
            });
          },
        );
      }

      if (previousCase) {
        queryClient.setQueryData<CaseResponse>(
          casesKeys.get(wsId, variables.id),
          (oldCase) => {
            return produce(oldCase, (draft) => {
              if (!draft?.data) return;

              const hasAssigneeChanged = variables.assigneeId !== undefined;

              if (hasAssigneeChanged && draft.data.relationships) {
                draft.data.relationships.assignee = updateAssignee(
                  variables.assigneeId,
                );
              }

              // Increment etag - API sends strings but type says number
              if (draft.data.attributes?.etag !== undefined) {
                (draft.data.attributes as { etag: string | number }).etag =
                  incrementEtag(draft.data.attributes.etag);
              }
            });
          },
        );
      }

      return {
        previousQueueCases,
        previousCase,
        previousCaseQueueSummary,
        previousEntityCases,
      };
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, variables.id),
      });
      // We do not invalidate the queue cases list because queue cases
      // don't represent the actual state of cases because of eventual consistency.
      // In case of invalidation it will break the optimistic update.
    },
    onError: (_, variables, context) => {
      if (context?.previousQueueCases) {
        queryClient.setQueryData(
          caseQueuesKeys.cases(
            wsId,
            currentQueueId,
            environment,
            filters,
            sort,
          ),
          context.previousQueueCases,
        );
      }

      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, variables.id),
          context.previousCase,
        );
      }

      if (context?.previousCaseQueueSummary) {
        queryClient.setQueryData(
          caseQueuesKeys.summary(wsId, currentQueueId, environment),
          context.previousCaseQueueSummary,
        );
      }

      context?.previousEntityCases?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
  });
};

type BulkUpdateContext = {
  previousQueueCases?: InfiniteData<CasesResponse>;
  previousCaseQueueSummary?: CaseQueueSummary;
};

type BulkCaseAssignParams = {
  operation: "assign";
  caseIds: string[];
  assigneeId: Nullable<string>;
  queueId: string;
};

type BulkCaseArchiveParams = {
  caseIds: string[];
  operation: "archive";
  queueId: string;
};

type BulkCaseUpdateParams = BulkCaseArchiveParams | BulkCaseAssignParams;

type BulkUpdateResult = {
  successfulCaseIds: string[];
  failedCaseIds: string[];
};

export const useBulkCaseUpdate = (queueId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();
  const [environment] = useEnvironment();
  const userId = useCurrentUserId();
  const { filterParams: filters, sorting } = useCasesQueueParams(queueId);
  const sort = sortingToApiSortParam(sorting);

  return useMutation<
    BulkUpdateResult,
    AxiosError,
    BulkCaseUpdateParams,
    BulkUpdateContext
  >({
    mutationFn: async ({ queueId, ...params }) => {
      const atomicOperations: CaseBulkAtomicOperationsRequestAtomicOperationsInner[] =
        params.operation === "assign"
          ? params.caseIds.map((caseId) => ({
              op: "update",
              ref: {
                type: "cases",
                id: caseId,
                relationship: "assignee",
              },
              data: params.assigneeId
                ? {
                    type: "assignees",
                    id: params.assigneeId,
                  }
                : null,
            }))
          : params.caseIds.map((caseId) => ({
              op: "update",
              ref: {
                type: "cases",
                id: caseId,
                relationship: "archive",
              },
              meta: {
                action: "archive",
              },
            }));

      const { results, batchCaseIds } = await executeBulkOperationsInBatches(
        apiClient,
        queueId,
        params.caseIds,
        atomicOperations,
      );

      const successfulCaseIds: string[] = [];
      const failedCaseIds: string[] = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successfulCaseIds.push(...batchCaseIds[index]);
        } else {
          failedCaseIds.push(...batchCaseIds[index]);
        }
      });

      return { successfulCaseIds, failedCaseIds };
    },
    onMutate: async (variables) => {
      // Cancel in-flight refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: caseQueuesKeys.cases(
          wsId,
          variables.queueId,
          environment,
          filters,
          sort,
        ),
      });
      await queryClient.cancelQueries({
        queryKey: caseQueuesKeys.summary(wsId, variables.queueId, environment),
      });

      const previousQueueCases = queryClient.getQueryData<
        InfiniteData<CasesResponse>
      >(
        caseQueuesKeys.cases(
          wsId,
          variables.queueId,
          environment,
          filters,
          sort,
        ),
      );

      const previousCaseQueueSummary =
        queryClient.getQueryData<CaseQueueSummary>(
          caseQueuesKeys.summary(wsId, variables.queueId, environment),
        );

      if (previousQueueCases && variables.operation === "archive") {
        const removeCases = (
          page: CasesResponse,
          idx: number,
          pages: CasesResponse[],
        ) => {
          pages[idx].data = page.data.filter(
            (caseData) => !variables.caseIds.includes(caseData.id),
          );
        };

        queryClient.setQueryData<InfiniteData<CasesResponse>>(
          caseQueuesKeys.cases(
            wsId,
            variables.queueId,
            environment,
            filters,
            sort,
          ),
          (oldCases) => {
            return produce(oldCases, (draft) => {
              draft?.pages.forEach(removeCases);
            });
          },
        );
      }

      if (previousQueueCases && variables.operation === "assign") {
        const updateAssignees = (page: CasesResponse) => {
          page.data.forEach((caseData) => {
            const isAffected = variables.caseIds.includes(caseData.id);
            const hasAssigneeChanged = variables.assigneeId !== undefined;

            if (
              isAffected &&
              hasAssigneeChanged &&
              caseData.relationships?.assignee
            ) {
              caseData.relationships.assignee = updateAssignee(
                variables.assigneeId,
              );
            }

            // Increment etag for affected cases
            if (isAffected && caseData.attributes?.etag !== undefined) {
              (caseData.attributes as { etag: string | number }).etag =
                incrementEtag(caseData.attributes.etag);
            }
          });
        };

        queryClient.setQueryData<InfiniteData<CasesResponse>>(
          caseQueuesKeys.cases(
            wsId,
            variables.queueId,
            environment,
            filters,
            sort,
          ),
          (oldCases) => {
            return produce(oldCases, (draft) => {
              draft?.pages.forEach(updateAssignees);
            });
          },
        );

        const affectedCases =
          previousQueueCases?.pages.flatMap((page) =>
            page.data.filter((c) => variables.caseIds.includes(c.id)),
          ) ?? [];

        const previouslyUnassignedCount = affectedCases.filter(
          (c) => !c.relationships?.assignee.data,
        ).length;

        const ownedByCurrentUser = affectedCases.filter(
          (c) => c.relationships?.assignee.data?.id === userId,
        ).length;

        if (previousCaseQueueSummary) {
          const isAssignment = !!variables.assigneeId;
          const isUnassignment = variables.assigneeId === null;

          queryClient.setQueryData<CaseQueueSummary>(
            caseQueuesKeys.summary(wsId, variables.queueId, environment),
            (oldSummary) => {
              return produce(oldSummary, (draft) => {
                if (!draft) return;

                if (isAssignment) {
                  draft.attributes.unassigned -= previouslyUnassignedCount;
                  if (variables.assigneeId === userId) {
                    draft.attributes.assigned_to_user +=
                      variables.caseIds.length - ownedByCurrentUser;
                  } else {
                    draft.attributes.assigned_to_user -= ownedByCurrentUser;
                  }
                } else if (isUnassignment) {
                  draft.attributes.unassigned +=
                    variables.caseIds.length - previouslyUnassignedCount;
                  draft.attributes.assigned_to_user -= ownedByCurrentUser;
                }
              });
            },
          );
        }
      }

      return { previousQueueCases, previousCaseQueueSummary };
    },
    onSuccess: (data, variables) => {
      if (data.failedCaseIds.length > 0) {
        toastActions.failure({
          title: "Bulk update partially failed",
          description: `Failed to update ${data.failedCaseIds.length} out of ${
            data.failedCaseIds.length + data.successfulCaseIds.length
          } cases.`,
        });

        queryClient.invalidateQueries({
          queryKey: caseQueuesKeys.cases(
            wsId,
            variables.queueId,
            environment,
            filters,
            sort,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: caseQueuesKeys.summary(
            wsId,
            variables.queueId,
            environment,
          ),
        });
      }
    },
    onError: (_, variables, context) => {
      if (context?.previousQueueCases) {
        queryClient.setQueryData(
          caseQueuesKeys.cases(
            wsId,
            variables.queueId,
            environment,
            filters,
            sort,
          ),
          context.previousQueueCases,
        );
      }

      if (context?.previousCaseQueueSummary) {
        queryClient.setQueryData(
          caseQueuesKeys.summary(wsId, variables.queueId, environment),
          context.previousCaseQueueSummary,
        );
      }
    },
  });
};

type BlockConfigPatch =
  | RelatedEntitiesBlockConfigPatch
  | RelatedEventsBlockConfigPatch;

type BlockConfigPatchResponse =
  | RelatedEntitiesBlockConfigPatchResponse
  | RelatedEventsBlockConfigPatchResponse;

type BlockConfigPatchAttributes =
  | RelatedEntitiesCaseBlockConfigPatchAttributes
  | RelatedEventsCaseBlockConfigPatchAttributes;

type UpdateBlockConfigParams = {
  caseId: string;
  blockKey: string;
  attributes: BlockConfigPatchAttributes;
};

type UpdateBlockConfigContext = {
  previousCase?: CaseResponse;
};

const updateBlockConfigInCase = (
  draft: CaseResponse | undefined,
  caseId: string,
  blockKey: string,
  attributes: BlockConfigPatchAttributes,
) => {
  if (!draft?.included) return;

  const blockConfigId = `${caseId}#${blockKey}#config`;
  const blockConfig = draft.included.find(
    (item) => item.type === "case_block_configs" && item.id === blockConfigId,
  );

  if (blockConfig?.attributes) {
    Object.assign(blockConfig.attributes, attributes);
  }
};

export const useUpdateBlockConfig = () => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    BlockConfigPatchResponse,
    AxiosError,
    UpdateBlockConfigParams,
    UpdateBlockConfigContext
  >({
    mutationFn: async ({ caseId, blockKey, attributes }) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const blockConfigPatch = {
        data: {
          type: ModelsCaseDataApiModelsRelatedEntitiesBlockConfigPatchDataTypeEnum.CASE_BLOCK_CONFIGS,
          id: blockKey,
          attributes,
        },
      } as BlockConfigPatch;

      const response =
        await apiClient.patchCaseBlockConfigApiV1CasesCaseIdBlocksBlockKeyConfigPatch(
          {
            caseId,
            blockKey,
            payload1: blockConfigPatch as RelatedEventsBlockConfigPatch,
          },
        );

      return response.data;
    },
    onMutate: ({ caseId, blockKey, attributes }) => {
      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, caseId),
      );

      if (previousCase?.included) {
        queryClient.setQueryData<CaseResponse>(
          casesKeys.get(wsId, caseId),
          (oldCase) =>
            produce(oldCase, (draft) =>
              updateBlockConfigInCase(draft, caseId, blockKey, attributes),
            ),
        );
      }

      return { previousCase };
    },
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, caseId),
      });
    },
    onError: (_, { caseId }, context) => {
      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, caseId),
          context.previousCase,
        );
      }
    },
  });
};

const updateAssignee = (assigneeId?: string | null) => ({
  data: assigneeId
    ? {
        type: "assignees" as const,
        id: assigneeId,
      }
    : null,
});

type FileCategoryFileIds = Record<string, string[]>;

export const FILE_OPERATIONS = {
  UPLOAD: "upload",
  MOVE: "move",
  DELETE: "delete",
} as const;

const createFileCategoryOperation = (
  caseId: string,
  categoryKey: string,
  fileIds: string[],
): FileCategoriesAtomicOperation => ({
  op: FileCategoriesAtomicOperationOpEnum.UPDATE,
  data: {
    type: FileCategoriesAtomicResourceObjectTypeEnum.FILE_CATEGORIES,
    id: `${caseId}#${categoryKey}`,
    attributes: {
      file_ids: fileIds,
    },
  },
});

type UploadFileParams = {
  type: typeof FILE_OPERATIONS.UPLOAD;
  caseId: string;
  file: File;
  categoryKey: string;
  env: UploadFileEnvFilesPostEnvEnum;
  source: FileSource;
  currentFileIds: FileCategoryFileIds;
};

type MoveFileParams = {
  type: typeof FILE_OPERATIONS.MOVE;
  caseId: string;
  fileId: string;
  fileName: string;
  fromCategoryKey: string;
  toCategoryKey: string;
  currentFileIds: FileCategoryFileIds;
};

type DeleteFileParams = {
  type: typeof FILE_OPERATIONS.DELETE;
  caseId: string;
  fileId: string;
  fileName: string;
  categoryKey: string;
  currentFileIds: FileCategoryFileIds;
};

type FileCategoryOperationParams =
  | UploadFileParams
  | MoveFileParams
  | DeleteFileParams;

type FileCategoryOperationResult =
  | { fileId: string; categoryResponse: FileCategoriesAtomicResultsResponse }
  | FileCategoriesAtomicResultsResponse;

type FileCategoryOperationContext = {
  previousCase?: CaseResponse;
};

export const useFileCategoryOperation = () => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();
  const { onFileSelect, onFileUpload } = useUploadFile();

  return useMutation<
    FileCategoryOperationResult,
    AxiosError,
    FileCategoryOperationParams,
    FileCategoryOperationContext
  >({
    mutationFn: async (params) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const { caseId, currentFileIds, type } = params;

      switch (type) {
        case FILE_OPERATIONS.UPLOAD: {
          const { file, categoryKey, env, source } = params;

          onFileSelect(file, categoryKey);
          const fileId = await onFileUpload(file, categoryKey, env, source);

          if (!fileId) {
            throw new Error("Upload failed");
          }

          const updatedFileIds = [
            ...(currentFileIds[categoryKey] || []),
            fileId,
          ];

          const request: FileCategoriesAtomicOperationsRequest = {
            "atomic:operations": [
              createFileCategoryOperation(caseId, categoryKey, updatedFileIds),
            ],
          };

          const response =
            await apiClient.applyAtomicFileCategoryOperationsApiV1CasesCaseIdFileCategoriesOperationsPost(
              {
                caseId,
                fileCategoriesAtomicOperationsRequest: request,
              },
            );

          return { fileId, categoryResponse: response.data };
        }

        case FILE_OPERATIONS.MOVE: {
          const { fileId, fromCategoryKey, toCategoryKey } = params;

          const operations = [
            createFileCategoryOperation(
              caseId,
              fromCategoryKey,
              (currentFileIds[fromCategoryKey] || []).filter(
                (id) => id !== fileId,
              ),
            ),
            createFileCategoryOperation(caseId, toCategoryKey, [
              ...(currentFileIds[toCategoryKey] || []),
              fileId,
            ]),
          ];

          const request: FileCategoriesAtomicOperationsRequest = {
            "atomic:operations": operations,
          };

          const response =
            await apiClient.applyAtomicFileCategoryOperationsApiV1CasesCaseIdFileCategoriesOperationsPost(
              {
                caseId,
                fileCategoriesAtomicOperationsRequest: request,
              },
            );

          return response.data;
        }

        case FILE_OPERATIONS.DELETE: {
          const { fileId, categoryKey } = params;

          const updatedFileIds = (currentFileIds[categoryKey] || []).filter(
            (id) => id !== fileId,
          );

          const request: FileCategoriesAtomicOperationsRequest = {
            "atomic:operations": [
              createFileCategoryOperation(caseId, categoryKey, updatedFileIds),
            ],
          };

          const response =
            await apiClient.applyAtomicFileCategoryOperationsApiV1CasesCaseIdFileCategoriesOperationsPost(
              {
                caseId,
                fileCategoriesAtomicOperationsRequest: request,
              },
            );

          return response.data;
        }

        default:
          return assertUnreachable(type);
      }
    },
    onMutate: ({ caseId }) => {
      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, caseId),
      );
      return { previousCase };
    },
    onSuccess: (_, { caseId, type }) => {
      queryClient.invalidateQueries({ queryKey: casesKeys.get(wsId, caseId) });
      const messages = {
        upload: "File uploaded successfully",
        move: "File moved successfully",
        delete: "File deleted successfully",
      };
      toastActions.success({ title: messages[type] });
    },
    onError: (_, params, context) => {
      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, params.caseId),
          context.previousCase,
        );
      }

      const fileName =
        params.type === FILE_OPERATIONS.UPLOAD
          ? params.file.name
          : params.fileName;
      toastActions.failure({
        description: `${capitalize(params.type)} failed for ${fileName}. Please try again.`,
        title: `File ${capitalize(params.type)} Failed`,
      });
    },
  });
};

type SaveCaseActionParams = {
  etag: string | number;
  actionId: string;
  actionFormId: string;
  actionFormValues: CaseActionFormValues;
};

export const useSaveCaseActionForm = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseActionFormPatchResponse,
    AxiosError,
    SaveCaseActionParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient.patchCaseActionFormStateApiV1CasesIdActionActionIdFormStatePatch(
          {
            actionId: extractActionIdParam(data.actionId),
            id: caseId,
            ifMatch: data.etag.toString(),
            caseActionFormPatchRequest: {
              data: {
                type: "case_action_forms" as const,
                id: data.actionFormId,
                attributes: data.actionFormValues as {
                  [key: string]: AttributesValue;
                },
              },
            },
          },
        );

      return response.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type SubmitCaseActionParams = {
  actionId: string;
  /**
   * ETag of the case.
   */
  etag: string;
  formEtag?: string | number;
};

export const useSubmitCaseAction = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    RunningActionsResponse,
    AxiosError,
    SubmitCaseActionParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient.runCaseActionApiV1CasesCaseIdActionsActionIdRunPost({
          actionId: data.actionId,
          caseId,
          ifMatch: data.etag.toString(),
          ifMatchForm: data.formEtag ? data.formEtag.toString() : null,
        });

      return response.data;
    },
    onSuccess: async () => {
      if (caseId) {
        await queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type DismissCaseActionParams = {
  actionId: string;
  etag: string | number;
};

export const useDismissCaseAction = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    RunningActionsResponse,
    AxiosError,
    DismissCaseActionParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient?.dismissCaseRunningActionApiV1CasesCaseIdRunningActionsRunningActionIdDismissPost(
          {
            runningActionId: data.actionId,
            caseId,
            ifMatch: data.etag.toString(),
          },
        );

      return response?.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type RetryCaseActionParams = {
  actionId: string;
  etag: string | number;
};

export const useRetryCaseAction = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<RunningActionsResponse, AxiosError, RetryCaseActionParams>(
    {
      mutationFn: async (data) => {
        if (!apiClient || !caseId) {
          throw new Error("API client or case ID is not available");
        }

        const response =
          await apiClient.retryCaseRunningActionApiV1CasesCaseIdRunningActionsRunningActionIdRetryPost(
            {
              caseId,
              runningActionId: data.actionId,
              ifMatch: data.etag.toString(),
            },
          );

        return response.data;
      },
      onSuccess: async () => {
        if (caseId) {
          await queryClient.invalidateQueries({
            queryKey: casesKeys.get(wsId, caseId),
          });
        }
      },
    },
  );
};

/**
 * Extracts action ID param from an action ID.
 * Example: For an action ID "action#param1#param2", it returns "param1#param2".
 */
const extractActionIdParam = (actionId: string) => {
  const [, ...paramPart] = actionId.split("#");
  return paramPart.join("#");
};

// Task-related mutations

type SaveCaseTaskFormParams = {
  taskKey: string;
  fullTaskId: string;
  /**
   * ETag of the form (for form data optimistic locking).
   */
  formEtag: string | number;
  taskFormId: string;
  taskFormValues: Record<string, unknown>;
};

export const useSaveCaseTaskForm = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTaskFormPatchResponse,
    AxiosError,
    SaveCaseTaskFormParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient.patchCaseTaskFormStateApiV1CasesCaseIdTasksTaskKeyFormDataPatch(
          {
            caseId,
            taskKey: TaskIdExtractor.getStatusAndTaskKey(data.fullTaskId),
            ifMatch: data.formEtag.toString(),
            caseTaskFormPatchRequest: {
              data: {
                type: "case_task_forms" as const,
                id: `${TaskIdExtractor.getCaseId(data.fullTaskId)}#${data.taskFormId}`,
                attributes: data.taskFormValues as {
                  [key: string]: AttributesValue;
                },
              },
            },
          },
        );

      return response.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type UpdateCaseTaskCompletionParams = {
  taskKey: string;
  taskId: string;
  isCompleted: boolean;
  /**
   * ETag of the case captured at call time. Only a fallback for when the case
   * is missing from the query cache — the mutation reads the fresh etag from
   * the cache at execution time.
   */
  etag: string | number;
};

type UpdateCaseTaskCompletionContext = {
  previousIsCompleted?: boolean;
};

const updateTaskCompletionMutationKey = (caseId?: string) =>
  ["cases", "updateTaskCompletion", caseId] as const;

const findCaseTaskResource = (
  caseResponse: CaseResponse | undefined,
  taskId: string,
) =>
  caseResponse?.included?.find(
    (resource) =>
      resource.type === "case_tasks" &&
      resource.id === TaskIdExtractor.getFullId(taskId),
  );

export const useUpdateCaseTaskCompletion = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();
  const caseQueryKey = caseId ? casesKeys.get(wsId, caseId) : undefined;

  const setCachedTaskCompletion = (taskId: string, isCompleted: boolean) => {
    if (!caseQueryKey) return;

    queryClient.setQueryData<CaseResponse>(caseQueryKey, (oldCase) =>
      produce(oldCase, (draft) => {
        const task = findCaseTaskResource(draft, taskId);
        if (task) {
          (task.attributes as CaseTaskAttributes).is_completed = isCompleted;
        }
      }),
    );
  };

  return useMutation<
    CaseTaskCompletionPatchResponse,
    AxiosError,
    UpdateCaseTaskCompletionParams,
    UpdateCaseTaskCompletionContext
  >({
    mutationKey: updateTaskCompletionMutationKey(caseId),
    // Serialize task-completion PATCHes per case: they all send the case
    // etag as If-Match, so parallel requests would 412 each other.
    scope: { id: `case-task-completion:${wsId}:${caseId}` },
    onMutate: async (data) => {
      if (!caseQueryKey) return {};

      // Cancel in-flight case fetches so a stale response can't overwrite
      // the optimistic toggle or the etag while mutations are queued.
      await queryClient.cancelQueries({ queryKey: caseQueryKey });

      const previousIsCompleted = (
        findCaseTaskResource(
          queryClient.getQueryData<CaseResponse>(caseQueryKey),
          data.taskId,
        )?.attributes as CaseTaskAttributes | undefined
      )?.is_completed;

      setCachedTaskCompletion(data.taskId, data.isCompleted);

      return { previousIsCompleted };
    },
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      // Scoped mutations run one at a time, so the cache holds the etag
      // produced by the previous mutation — the etag captured at click time
      // would be stale for queued clicks.
      const cachedEtag = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, caseId),
      )?.data.attributes?.etag;

      const response =
        await apiClient.patchTaskCompletionStatusApiV1CasesCaseIdTasksTaskKeyPatch(
          {
            caseId,
            taskKey: TaskIdExtractor.getStatusAndTaskKey(data.taskId),
            ifMatch: (cachedEtag ?? data.etag).toString(),
            caseTaskCompletionPatchRequest: {
              data: {
                type: "case_tasks" as const,
                // Send full task ID: caseid#status#task_key
                id: TaskIdExtractor.getFullId(data.taskId),
                attributes: {
                  is_completed: data.isCompleted,
                },
              },
            },
          },
        );

      return response.data;
    },
    onSuccess: (_response, data) => {
      if (!caseQueryKey) return;

      // The PATCH response carries no case etag, but the server accepted our
      // If-Match, so the etag advanced — bump the cache so the next queued
      // mutation sends the current value without waiting for a refetch.
      queryClient.setQueryData<CaseResponse>(caseQueryKey, (oldCase) =>
        produce(oldCase, (draft) => {
          // Increment etag - API sends strings but type says number
          if (draft?.data.attributes?.etag !== undefined) {
            (draft.data.attributes as { etag: string | number }).etag =
              incrementEtag(draft.data.attributes.etag);
          }
        }),
      );

      setCachedTaskCompletion(data.taskId, data.isCompleted);
    },
    onError: async (_error, data, context) => {
      // Revert only this task: a full snapshot rollback would clobber the
      // optimistic updates of mutations queued behind this one.
      if (context?.previousIsCompleted !== undefined) {
        setCachedTaskCompletion(data.taskId, context.previousIsCompleted);
      }

      if (!caseQueryKey) return;

      // Awaited so a genuine conflict refetches the real etag before the
      // next queued mutation fires, instead of cascading into more 412s.
      // With nothing queued behind, the drain invalidation in onSettled
      // reconciles instead.
      const hasQueuedMutations =
        queryClient.isMutating({
          mutationKey: updateTaskCompletionMutationKey(caseId),
        }) > 1;
      if (hasQueuedMutations) {
        await queryClient.invalidateQueries({ queryKey: caseQueryKey });
      }
    },
    onSettled: () => {
      if (!caseQueryKey) return;

      // Reconcile with the server once the per-case queue drains; this
      // mutation still counts as pending here, so 1 means it's the last one.
      const pendingMutations = queryClient.isMutating({
        mutationKey: updateTaskCompletionMutationKey(caseId),
      });
      if (pendingMutations === 1) {
        void queryClient.invalidateQueries({ queryKey: caseQueryKey });
      }
    },
  });
};

export const useIsUpdatingCaseTaskCompletion = (caseId?: string): boolean =>
  useIsMutating({ mutationKey: updateTaskCompletionMutationKey(caseId) }) > 0;

type BulkCaseActionParams = {
  caseIds: string[];
  actionKey: string;
  formData?: Record<string, unknown>;
};

export type BulkActionCaseFailure = {
  caseId: string;
  error?: string;
};

type BulkCaseActionResult = {
  successfulCaseIds: string[];
  failures: BulkActionCaseFailure[];
};
export const useRunBulkCaseActions = ({
  queueId,
  wsId,
}: {
  queueId: string;
  wsId: string;
}) => {
  const queryClient = useQueryClient();
  const apiClient = useCasesClient();

  return useMutation<BulkCaseActionResult, AxiosError, BulkCaseActionParams>({
    mutationFn: async ({ caseIds, actionKey, formData }) => {
      const atomicOperations: CaseBulkAtomicOperationsRequestAtomicOperationsInner[] =
        caseIds.map((caseId) => ({
          op: CaseRunActionAtomicOperationOpEnum.UPDATE,
          ref: {
            type: CaseBulkRefTypeEnum.CASES,
            id: caseId,
            relationship: "running_action",
          },
          meta: {
            action_key: actionKey,
            form_data: formData || null,
          },
        }));

      const { results, batchCaseIds } = await executeBulkOperationsInBatches(
        apiClient,
        queueId,
        caseIds,
        atomicOperations,
      );

      const successfulCaseIds: string[] = [];
      const failures: BulkActionCaseFailure[] = [];

      results.forEach((result, index) => {
        const batchCaseIdList = batchCaseIds[index];

        if (result.status === "fulfilled") {
          successfulCaseIds.push(...batchCaseIdList);
        } else {
          const errorResponse = result.reason?.response?.data;
          const atomicResults = errorResponse?.["atomic:results"] as
            | JsonApiAtomicResult[]
            | undefined;

          if (atomicResults) {
            atomicResults.forEach((atomicResult, caseIndex) => {
              const caseId = batchCaseIdList[caseIndex];
              const error = atomicResult.errors?.[0];
              failures.push({
                caseId,
                error: error?.title,
              });
            });
          } else {
            batchCaseIdList.forEach((caseId) => {
              failures.push({ caseId });
            });
          }
        }
      });

      return { successfulCaseIds, failures };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: caseQueuesKeys.get(wsId, queueId),
      });
    },
  });
};

export const useCreateCase = () => {
  const apiClient = useCasesClient();

  return useMutation<CaseCreateAcceptedResponse, AxiosError, CaseCreateRequest>(
    {
      mutationFn: async (caseCreateRequest) => {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const response = await apiClient.createCaseApiV1CasesPost({
          idempotencyKey: v4(),
          caseCreateRequest,
        });

        return response.data;
      },
    },
  );
};
